import type { ReactNode } from "react";

export type Lang = "ru" | "kz";

export const T = {
  ru: {
    back: "Назад",
    needHelp: "Нужна помощь?",
    whatsappHint: "Если возникли вопросы, напишите нам в WhatsApp.",
    writeWhatsapp: "Написать в WhatsApp",
    safe: "Безопасно",
    fast: "Быстро",
    good: "Выгодно",
  },
  kz: {
    back: "Артқа",
    needHelp: "Көмек керек пе?",
    whatsappHint: "Сұрақтарыңыз болса, WhatsApp арқылы жазыңыз.",
    writeWhatsapp: "WhatsApp-қа жазу",
    safe: "Қауіпсіз",
    fast: "Жылдам",
    good: "Тиімді",
  },
} as const;

export function LangSwitch({ lang, onChange }: { lang: Lang; onChange: (l: Lang) => void }) {
  return (
    <div className="inline-flex rounded-full border border-neutral-200 bg-white p-1 text-sm font-semibold shadow-sm">
      <button
        onClick={() => onChange("ru")}
        className={`rounded-full px-3 py-1 transition ${lang === "ru" ? "bg-beeline-gold text-black" : "text-neutral-500"}`}
      >
        РУС
      </button>
      <button
        onClick={() => onChange("kz")}
        className={`rounded-full px-3 py-1 transition ${lang === "kz" ? "bg-beeline-gold text-black" : "text-neutral-500"}`}
      >
        ҚАЗ
      </button>
    </div>
  );
}

export function BeelineHeader({ lang, onLangChange, onBack }: { lang: Lang; onLangChange: (l: Lang) => void; onBack?: () => void }) {
  return (
    <div className="flex items-center justify-between px-5 pt-5">
      {onBack ? (
        <button onClick={onBack} className="flex items-center gap-1 text-sm font-medium text-neutral-700">
          ← {T[lang].back}
        </button>
      ) : (
        <span />
      )}
      <LangSwitch lang={lang} onChange={onLangChange} />
    </div>
  );
}

export function BeelineBrand() {
  return (
    <div className="flex flex-col items-center px-6 pt-2 text-center">
      <div
        className="h-16 w-16 rounded-full shadow-md"
        style={{
          background:
            "repeating-linear-gradient(45deg, #181818 0 10px, #FFC900 10px 20px)",
        }}
        aria-hidden
      />
      <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-neutral-900">BEELINE</h1>
      <div className="mt-1 flex items-center gap-2 text-xs font-semibold tracking-widest text-beeline-amber">
        <span className="h-px w-6 bg-beeline-amber" /> ПО ВЫГОДНЫМ ЦЕНАМ <span className="h-px w-6 bg-beeline-amber" />
      </div>
      <div className="mt-1 text-lg font-bold">
        GEO <span className="text-beeline-amber">MASTER</span>
      </div>
    </div>
  );
}

export function WhatsAppCta({ lang, whatsappUrl }: { lang: Lang; whatsappUrl: string }) {
  return (
    <div className="mx-5 mt-6 flex flex-col gap-3 rounded-2xl bg-emerald-50 p-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <div className="font-bold text-neutral-900">{T[lang].needHelp}</div>
        <div className="text-sm text-neutral-500">{T[lang].whatsappHint}</div>
      </div>
      <a
        href={whatsappUrl}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-500 px-4 py-2.5 font-semibold text-white transition hover:bg-emerald-600"
      >
        {T[lang].writeWhatsapp} →
      </a>
    </div>
  );
}

export function TrustBadges({ lang }: { lang: Lang }) {
  return (
    <div className="mx-5 mt-6 flex justify-between text-center text-xs text-neutral-500">
      <span>🛡️ {T[lang].safe}</span>
      <span>⚡ {T[lang].fast}</span>
      <span>👍 {T[lang].good}</span>
    </div>
  );
}

export function PrimaryButton({
  children,
  onClick,
  disabled,
  variant = "gold",
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: "gold" | "kaspi" | "neutral";
}) {
  const styles = {
    gold: "bg-beeline-gold text-black hover:brightness-95",
    kaspi: "bg-red-600 text-white hover:bg-red-700",
    neutral: "bg-neutral-100 text-neutral-400",
  }[disabled ? "neutral" : variant];
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex w-full items-center justify-center gap-2 rounded-2xl px-6 py-4 text-base font-bold transition ${styles} disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}

export function Spinner({ size = 56 }: { size?: number }) {
  return (
    <div
      className="spin rounded-full border-4 border-beeline-gold/30 border-t-beeline-gold"
      style={{ width: size, height: size }}
      aria-label="Загрузка"
      role="status"
    />
  );
}
